import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ClipboardList, Calendar, Award, ArrowRight, BookOpen, Sparkles } from "lucide-react";
import { api } from "../lib/api";

/**
 * Student dashboard strip — "My Assignments". Shows every assignment from
 * every group the student belongs to, marked done when progress shows it.
 */
export default function MyAssignments({ lang = "en" }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancel = false;
    api.get("/me/assignments")
      .then((r) => { if (!cancel) setData(r.data); })
      .catch(() => {})
      .finally(() => { if (!cancel) setLoading(false); });
    return () => { cancel = true; };
  }, []);

  if (loading || !data || (data.assignments || []).length === 0) return null;

  const assignments = data.assignments;
  const pending = assignments.filter((a) => a.status !== "done");
  const done = assignments.length - pending.length;
  const completionPct = Math.round((done / assignments.length) * 100);

  const L = lang === "ar";

  return (
    <section className="mb-8 animate-fade-up" data-testid="my-assignments">
      <div className="rounded-2xl border border-terracotta/30 bg-gradient-to-br from-terracotta/5 via-white to-sage/5 p-5 sm:p-6">
        <div className="flex items-start gap-4 flex-wrap">
          <div className="w-11 h-11 rounded-xl bg-terracotta text-white flex items-center justify-center shrink-0">
            <ClipboardList className="w-5 h-5" strokeWidth={1.8} />
          </div>
          <div className="flex-1 min-w-[180px]">
            <div className="text-[10px] uppercase tracking-[0.2em] text-terracotta font-bold mb-1">
              {L ? "مهام المعلّم" : "From your teacher"}
            </div>
            <h2 className="font-display text-lg sm:text-xl font-semibold tracking-tight">
              {L ? "مهامي" : "My assignments"}
            </h2>
            <div className="text-xs text-muted-foreground mt-1">
              {L ? `${done} من ${assignments.length} مكتملة` : `${done} of ${assignments.length} complete`}
              {data.groups.length > 0 && (
                <> · <span className="text-sage font-medium">{data.groups.length} {L ? "مجموعة" : data.groups.length === 1 ? "group" : "groups"}</span></>
              )}
            </div>
          </div>
          <div className="inline-flex items-center gap-2 rounded-full bg-white border border-border px-3 py-1.5 text-xs font-semibold text-sage">
            <Sparkles className="w-3.5 h-3.5" /> {completionPct}% {L ? "مكتمل" : "done"}
          </div>
        </div>

        <div className="mt-5 grid sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="my-assignments-list">
          {assignments.slice(0, 6).map((a) => (
            <AssignmentCard key={a.assignment_id} a={a} lang={lang} />
          ))}
        </div>

        {assignments.length > 6 && (
          <div className="mt-3 text-xs text-muted-foreground">
            +{assignments.length - 6} {L ? "أخرى" : "more"}
          </div>
        )}
      </div>
    </section>
  );
}

const TYPE_COLOR_MAP = {
  quiz: "bg-sage/10 text-sage",
  teacher_quiz: "bg-sage text-white",
  flashcards: "bg-amber-100 text-amber-700",
  homework: "bg-terracotta/10 text-terracotta",
};
const TYPE_LABEL_AR = {
  quiz: "اختبار",
  teacher_quiz: "اختبار المعلّم",
  flashcards: "مراجعة بطاقات",
  homework: "واجب",
};
const TYPE_LABEL_EN = {
  quiz: "quiz",
  teacher_quiz: "custom quiz",
  flashcards: "flashcards",
  homework: "homework",
};

/** Resolve the route a student should be sent to when they click the card. */
function assignmentRoute(a) {
  if (a.type === "teacher_quiz") return `/assignment/${a.assignment_id}/quiz`;
  if (a.type === "homework") return `/assignment/${a.assignment_id}/homework`;
  if (!a.chapter_id) return null;
  const kindSegment = a.type === "quiz" ? "quiz" : a.type === "flashcards" ? "flashcards" : "lesson";
  return `/chapter/${a.chapter_id}?view=${kindSegment}`;
}

function assignmentStatusLabel(a, L) {
  const done = a.status === "done" || a.status === "reviewed";
  if (done) return a.score != null ? `${a.score}%` : (L ? "تم" : "Done");
  if (a.status === "submitted") return L ? "قيد المراجعة" : "Submitted";
  return null;
}

function cardBorderClass(status) {
  if (status === "done" || status === "reviewed") return "border-sage/30 opacity-80";
  if (status === "submitted") return "border-amber-300/60";
  return "border-border hover:border-sage/50";
}

function AssignmentCard({ a, lang }) {
  const L = lang === "ar";
  const done = a.status === "done" || a.status === "reviewed";
  const submitted = a.status === "submitted";
  const isTeacherQuiz = a.type === "teacher_quiz";

  const to = assignmentRoute(a);
  const typeColor = TYPE_COLOR_MAP[a.type] || TYPE_COLOR_MAP.homework;
  const typeLabel = L ? (TYPE_LABEL_AR[a.type] || a.type) : (TYPE_LABEL_EN[a.type] || a.type);
  const statusLabel = assignmentStatusLabel(a, L);
  const cardClasses = `block bg-white border rounded-xl p-4 hover:shadow-sm transition-all ${cardBorderClass(a.status)}`;

  const inner = (
    <>
      <div className="flex items-center gap-2 mb-2">
        <span className={`text-[10px] uppercase tracking-[0.18em] font-bold px-2 py-0.5 rounded-full ${typeColor}`}>
          {typeLabel}
        </span>
        {a.chapter_number && (
          <span className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground font-semibold">
            Ch.{a.chapter_number}
          </span>
        )}
        {isTeacherQuiz && a.question_count && (
          <span className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground font-semibold">
            {a.question_count}Q
          </span>
        )}
        {statusLabel && (
          <span className={`ms-auto inline-flex items-center gap-1 text-[10px] font-semibold ${
            done ? "text-sage" : "text-amber-700"
          }`}>
            <Award className="w-3 h-3" />
            {statusLabel}
          </span>
        )}
      </div>
      <div className="text-sm font-medium leading-tight line-clamp-2">{a.title}</div>
      <div className="text-[11px] text-muted-foreground mt-1.5 line-clamp-1 flex items-center gap-1.5">
        {a.due_date && (
          <><Calendar className="w-3 h-3" /> {a.due_date}</>
        )}
        {!a.due_date && a.group_name && (
          <><BookOpen className="w-3 h-3" /> {a.group_name}</>
        )}
      </div>
      {to && !done && (
        <div className="mt-3 inline-flex items-center gap-1 text-xs text-sage font-medium">
          {submitted ? (L ? "عرض" : "View") : (L ? "ابدأ" : "Start")} <ArrowRight className="w-3 h-3 rtl:rotate-180" />
        </div>
      )}
    </>
  );

  if (to) {
    return (
      <Link to={to} data-testid={`assignment-card-${a.assignment_id}`} className={cardClasses}>
        {inner}
      </Link>
    );
  }
  return (
    <div data-testid={`assignment-card-${a.assignment_id}`} className={cardClasses}>
      {inner}
    </div>
  );
}
